import React, { useState, useEffect } from "react";
import { getFirestore, collection, query, getDocs, orderBy, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import "../../styles/admin/UserManagement.css";

interface User {
  userId: string;
  email: string;
  displayName: string;
  role: string;
  createdAt: Date;
  isBlocked?: boolean;
}

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const db = getFirestore();
  const functions = getFunctions();

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const q = query(collection(db, "users"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);

      const userList: User[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        userList.push({
          userId: doc.id,
          email: data.email || "",
          displayName: data.displayName || "",
          role: data.role || "END_USER",
          createdAt: data.createdAt?.toDate?.() || new Date(),
          isBlocked: data.isBlocked || false,
        });
      });

      setUsers(userList);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
      console.error("Error loading users:", err);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Role filter
    if (roleFilter !== "ALL") {
      filtered = filtered.filter((u) => u.role === roleFilter);
    }

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.email.toLowerCase().includes(term) ||
          u.displayName.toLowerCase().includes(term) ||
          u.userId.toLowerCase().includes(term)
      );
    }

    setFilteredUsers(filtered);
  };

  const handlePromoteToAdmin = async (userId: string, email: string) => {
    if (!window.confirm(`Promote ${email} to ADMIN?`)) return;

    setActionLoading(userId);
    try {
      const promoteToAdminFn = httpsCallable(functions, "promote_to_admin");
      const result = await promoteToAdminFn({ userId });

      if ((result.data as any).success) {
        await loadUsers();
        alert(`✅ ${email} promoted to ADMIN`);
      } else {
        throw new Error((result.data as any).error || "Promotion failed");
      }
    } catch (err) {
      alert(
        `❌ Error: ${err instanceof Error ? err.message : "Failed to promote user"}`
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handlePromoteToClub = async (userId: string, email: string) => {
    if (!window.confirm(`Promote ${email} to CLUB?`)) return;

    setActionLoading(userId);
    try {
      const promoteToClubFn = httpsCallable(functions, "promote_to_club");
      const result = await promoteToClubFn({ userId });

      if ((result.data as any).success) {
        await loadUsers();
        alert(`✅ ${email} promoted to CLUB`);
      } else {
        throw new Error((result.data as any).error || "Promotion failed");
      }
    } catch (err) {
      alert(`❌ Error: ${err instanceof Error ? err.message : "Failed to promote user"}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "CLUB":
        return "#e74c3c";
      case "ADMIN":
        return "#3498db";
      case "STAFF":
        return "#f39c12";
      default:
        return "#95a5a6";
    }
  };

  return (
    <div className="user-management-page">
      <div className="page-header">
        <h1>👥 User Management</h1>
        <p className="page-subtitle">
          Manage club members, change roles, and view user details
        </p>
      </div>

      <div className="management-container">
        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{users.length}</div>
            <div className="stat-label">Total Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {users.filter((u) => u.role === "CLUB").length}
            </div>
            <div className="stat-label">Club Admins</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {users.filter((u) => u.role === "ADMIN").length}
            </div>
            <div className="stat-label">Staff</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {users.filter((u) => u.role === "END_USER").length}
            </div>
            <div className="stat-label">Members</div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <input
            type="text"
            placeholder="🔍 Search by email, name, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="role-filter"
          >
            <option value="ALL">All Roles</option>
            <option value="CLUB">Club Admin</option>
            <option value="ADMIN">Staff</option>
            <option value="END_USER">Member</option>
          </select>

          <button onClick={loadUsers} disabled={loading} className="btn-refresh">
            🔄 Refresh
          </button>
        </div>

        {/* Users Table */}
        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading-spinner">⏳ Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <p>No users found</p>
          </div>
        ) : (
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Member Since</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.userId} className={`user-row role-${user.role.toLowerCase()}`}>
                    <td className="name-cell">
                      <div className="name-info">
                        <div className="name">{user.displayName || "—"}</div>
                        <div className="id">{user.userId.substring(0, 8)}...</div>
                      </div>
                    </td>

                    <td className="email-cell">{user.email}</td>

                    <td className="role-cell">
                      <span
                        className="role-badge"
                        style={{ backgroundColor: getRoleColor(user.role) }}
                      >
                        {user.role}
                      </span>
                    </td>

                    <td className="date-cell">
                      {user.createdAt.toLocaleDateString("no-NO")}
                    </td>

                    <td className="actions-cell">
                      {user.role === "END_USER" && (
                        <button
                          className="btn-action btn-promote"
                          onClick={() =>
                            handlePromoteToAdmin(user.userId, user.email)
                          }
                          disabled={actionLoading === user.userId}
                          title="Promote to Staff"
                        >
                          {actionLoading === user.userId ? "⏳" : "⬆️ Staff"}
                        </button>
                      )}

                      {user.role === "ADMIN" && (
                        <button
                          className="btn-action btn-promote-club"
                          onClick={() =>
                            handlePromoteToClub(user.userId, user.email)
                          }
                          disabled={actionLoading === user.userId}
                          title="Promote to Club Admin"
                        >
                          {actionLoading === user.userId ? "⏳" : "⬆️ Club"}
                        </button>
                      )}

                      {user.role === "CLUB" && (
                        <span className="badge-badge">👑 Owner</span>
                      )}

                      <button
                        className="btn-action btn-view"
                        title="View details"
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary */}
        <div className="summary-section">
          <p>
            Showing {filteredUsers.length} of {users.length} users
          </p>
        </div>
      </div>
    </div>
  );
}
